import { useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { cn } from '@/lib/utils.ts'

import { MentionInputComponent, getMentionSuggestionsStyle } from '@/components/Mention.tsx'
import { withSortable } from '@/components/enhancers/withSortable.tsx'
import { TEMPORARY_ID_PREFIX } from '@/constants/PREFIX.ts'
import { useTaskMutation } from '@/requests/mutations/useMutateEntity.ts'
import { getTemporaryId, isEntityUpdate, isTemporaryId } from '@/utils/key.ts'
import { onEnterInclusive } from '@/utils/keyboard.ts'
import { identifyUsername } from '@/utils/string.ts'

type Props = {
  modes: AvailableMode[]
  task: Task
  placeholder?: string
}

export const Task = ({ modes, task, placeholder }: Props) => {
  const [title, setTitle] = useState(task.title)
  const [isEditMode, setIsEditMode] = useState(modes[0] === 'edit')
  const { create, update, remove } = useTaskMutation(task.id)
  const [isFocused, setIsFocused] = useState(false)

  const onSubmit = () => {
    if (!title) return

    if (isEntityUpdate(task)) {
      update({ title })
      setIsEditMode(false)
    } else {
      const newTaskId = getTemporaryId()
      create({ ...task, id: newTaskId, title })
      setTitle('')
      document.getElementById(`task-content-input-${TEMPORARY_ID_PREFIX}${task.storyId}`)?.focus()
    }
  }

  const onCancel = () => {
    setTitle(task.title)
    setIsEditMode(false)
  }

  if (isTemporaryId(task.id) && !task.__typename) {
    return (
      <div className="flex w-full gap-2 items-center justify-between py-1 px-2 rounded-xs bg-surface-neutralMedium">
        {isFocused || title.trim().length > 0 ? (
          <Checkbox
            id={`task-${task.id}`}
            name={`task-${task.id}`}
            checked={task.finished}
            variant="custom"
            size="md"
            className="rounded-2xs"
            disabled={true}
          />
        ) : (
          <img src="/images/entity-add-icon.svg" alt="add task icon" />
        )}

        <div className="flex-1 min-w-0 relative">
          <MentionableTask
            taskId={task.id}
            title={title}
            placeholder={placeholder}
            setTitle={setTitle}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            onSubmit={onSubmit}
            onCancel={onCancel}
          />
        </div>

        {(isFocused || title.trim().length > 0) && (
          <Button
            type="button"
            variant="secondary"
            className="flex-shrink-0 py-1 px-[3px] min-w-[43px] w-[43px] h-8 rounded-xs font-EN-KR-body2-bold"
            onClick={onSubmit}
            disabled={!title}
          >
            {isEntityUpdate(task) ? 'Save' : 'Add'}
          </Button>
        )}
      </div>
    )
  }

  return (
    <>
      {isEditMode ? (
        <div className="flex gap-2 items-center justify-between py-1 px-2 rounded-xs bg-surface-neutralMedium">
          <Checkbox
            id={`task-${task.id}`}
            name={`task-${task.id}`}
            checked={task.finished}
            variant="custom"
            size="md"
            className="rounded-2xs"
            disabled={true}
          />
          <div className="flex-1 min-w-0 relative">
            <MentionableTask
              taskId={task.id}
              title={title}
              placeholder={placeholder}
              setTitle={setTitle}
              isFocused={isFocused}
              setIsFocused={setIsFocused}
              onSubmit={onSubmit}
              onCancel={onCancel}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            className="flex-shrink-0 py-1 px-[3px] w-[43px] h-8 rounded-xs font-EN-KR-body2-bold"
            onClick={onSubmit}
            disabled={!title}
          >
            {isEntityUpdate(task) ? 'Save' : 'Add'}
          </Button>
        </div>
      ) : (
        <div className="flex min-h-10 gap-2 items-center justify-between py-1 px-2 rounded-xs bg-surface-neutralMedium">
          <Checkbox
            id={`task-${task.id}`}
            name={`task-${task.id}`}
            checked={task.finished}
            variant="custom"
            size="md"
            className="rounded-2xs"
            onClick={() => update({ finished: !task.finished })}
          />
          {/*biome-ignore lint/a11y/useKeyWithClickEvents: */}
          <span
            className="flex-1 min-w-0 font-EN-KR-body2-regular text-contents-primary cursor-pointer break-all"
            onClick={() => {
              if (modes.includes('edit')) {
                setIsEditMode(true)
                setTimeout(() => document.getElementById(`task-content-input-${task.id}`)?.focus())
              }
            }}
          >
            {identifyUsername(task.title)}
          </span>
          <Button type="button" size="icon" variant="icon" className="flex-none" onClick={() => remove(task.id)}>
            <img className="rounded-xs" src="/images/entity-delete-icon.svg" alt="delete task" />
          </Button>
        </div>
      )}
    </>
  )
}

type MentionableTaskProps = {
  taskId: string
  title: string
  placeholder?: string
  setTitle: (title: string) => void
  isFocused: boolean
  setIsFocused: (isFocused: boolean) => void
  onSubmit: () => void
  onCancel: () => void
}

const MentionableTask = ({
  taskId,
  title,
  placeholder,
  setTitle,
  setIsFocused,
  onSubmit,
  onCancel,
}: MentionableTaskProps) => {
  return (
    <MentionInputComponent
      id={`task-content-input-${taskId}`}
      value={title}
      onChange={(e: { target: { value: string } }) => {
        if (e.target.value === '\n') {
          setTitle('')
          return
        }
        setTitle(e.target.value)
      }}
      placeholder={placeholder}
      style={getMentionSuggestionsStyle({
        suggestions: {
          list: {
            position: 'absolute',
            marginTop: '10px',
            marginLeft: '7px',
            width: 'max-content',
          },
        },
      })}
      classNames={{
        'mentions__highlighter': '!font-EN-KR-body2-regular',
        'mentions__control': cn(
          'w-full p-2 flex flex-row items-center rounded-xs',
          '!font-EN-KR-body2-regular text-contents-primary wrap-break-word z-50',
        ),
        'mentions__input': cn(
          '!font-EN-KR-body2-regular w-full p-2 rounded-xs placeholder:!text-contents-secondary wrap-break-word',
          '!ring-0 !ring-offset-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-border-default-neutral focus-visible:!outline-border-default-focused focus-visible:!bg-surface-backgroundElevated focus-visible:placeholder:!text-contents-placeholder',
        ),
      }}
      allowSuggestionsAboveCursor
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.stopPropagation()
        onEnterInclusive(e, onSubmit)
        if (e.key === 'Escape') onCancel()
      }}
      onDragStart={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onDrag={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onDragEnd={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onMouseDown={(e) => {
        e.stopPropagation()
      }}
      onMouseMove={(e) => {
        e.stopPropagation()
      }}
      markup="**@__display__**"
    />
  )
}

export const SortableTask = withSortable(Task, {
  getId: (props) => props.task.id,
})
